import type { Ref } from 'vue'
import { nextTick, ref } from 'vue'

export type ErrorKey = string | number
export type ErrorKeyOrKeys = ErrorKey | ErrorKey[]

export type BackendError = {
    message: string
    path: ErrorKey[]
    isInternalServerError: boolean
    isUnauthorized: boolean
}

export interface ErrorCollection {
    readonly isInternalServerError: boolean
    readonly isUnauthorized: boolean
    // stopCollectingUnmapped: () => void

    get unmapped(): BackendError[]
    get length(): number

    has(path: ErrorKeyOrKeys): boolean

    children(path: ErrorKeyOrKeys): this

    get(path: ErrorKeyOrKeys): BackendError[] | undefined

    last(path: ErrorKeyOrKeys): string | undefined

    merge(withCollection: this): this
}

// interface ErrorCollectionWithPrivateMethods extends ErrorCollection {
//     // readonly displayed: ComputedRef<string[]>
//     collectDisplayed(key: string): void
//     debug: () => { errors: string[]; children: string[] }
// }

let id = 1
function getNextId(): number {
    return ++id
}

export function createErrorCollection(
    backendErrors: BackendError[],
    isInternalServerError = false,
    isUnauthorized = false,
    parent?: {
        key: string
        collectDisplayed(key: string): void
        // collection: ErrorCollectionWithPrivateMethods
    }
): ErrorCollection {
    const id = getNextId()
    const errors: Record<ErrorKey, BackendError[]> = {}
    const children: Record<ErrorKey, ErrorCollection> = {}

    const displayed: string[] = []
    let collectingAccessed = true

    function collectDisplayed(key: string): void {
        if (!displayed.includes(String(key))) {
            displayed.push(String(key))
        }

        if (parent) {
            parent.collectDisplayed(`${parent.key}.${key}`)
        }
    }

    const unmapped: Ref<BackendError[]> = ref([])
    nextTick(() => {
        collectingAccessed = false

        backendErrors.forEach((e) => {
            if (!displayed.includes(getKeys(e.path).join('.'))) {
                unmapped.value.push(e)
            }
        })
    })

    const collection: ErrorCollection = {
        // Internal
        // collectDisplayed,

        // Public
        // stopCollectingUnmapped(): void {
        //     if (collectingAccessed) {
        //         backendErrors.forEach((e) => {
        //             if (!displayed.includes(getKeys(e.path).join('.'))) {
        //                 unmapped.value.push(e)
        //             }
        //         })
        //
        //         Object.values(children.value).forEach((c) => c.stopCollectingUnmapped())
        //     }
        //
        //     collectingAccessed = false
        // },
        get unmapped(): BackendError[] {
            return unmapped.value
        },
        get length(): number {
            const errorLength = Object.values(errors).reduce((acc, curr) => {
                return acc + curr.length
            }, 0)
            const childLength = Object.values(children).reduce((acc, curr) => {
                return acc + curr.length
            }, 0)

            return errorLength + childLength
        },
        isInternalServerError:
            isInternalServerError ||
            backendErrors.some((e) => e.isInternalServerError),
        isUnauthorized:
            isUnauthorized || backendErrors.some((e) => e.isUnauthorized),
        has(path: ErrorKeyOrKeys): boolean {
            const keys = getKeys(path)
            const key = keys.shift()

            if (typeof key === 'undefined') {
                return false
            }

            if (!keys.length) {
                return !!errors[key]
            }

            const child = children[key]

            if (!child) {
                return false
            }

            return child.has(keys)
        },
        children(path: ErrorKeyOrKeys): ErrorCollection {
            const keys = getKeys(path)

            const key = keys.shift()

            if (typeof key === 'undefined') {
                return createErrorCollection(
                    errors[''] || [],
                    this.isInternalServerError,
                    this.isUnauthorized
                )
                // return {
                //     ...emptyErrors,
                //     isInternalServerError: this.isInternalServerError
                // }
            }

            const val: ErrorCollection | undefined = children[key]

            if (!val) {
                return createErrorCollection(
                    errors[''] || [],
                    this.isInternalServerError,
                    this.isUnauthorized
                )
                // return {
                //     ...emptyErrors,
                //     isInternalServerError: this.isInternalServerError
                // }
            }

            if (!keys.length) {
                return val
            }

            return val.children(keys)
        },
        get(path: ErrorKeyOrKeys): BackendError[] | undefined {
            const keys = getKeys(path)
            const keyString = keys.join('.')
            const key = keys.shift()

            if (typeof key === 'undefined') {
                return undefined
            }

            if (!keys.length) {
                if (collectingAccessed) {
                    collectDisplayed(String(key))
                    // displayed.value.push(String(key))
                }

                return errors[key]
            }

            const child = children[key]

            if (!child) {
                return undefined
            }

            const ret = child.get(keys)

            if (collectingAccessed && ret) {
                collectDisplayed(String(keyString))
                // displayed.value.push(keyString)
            }

            return ret
        },
        last(path: ErrorKeyOrKeys): string | undefined {
            const errors = this.get(path)

            if (errors && errors.length) {
                return errors[errors.length - 1]?.message
            }

            return undefined
        },
        merge(withCollection: ErrorCollection): typeof this {
            return mergeCollections([collection, withCollection])
        },
    }

    const childrenByKey: Record<ErrorKey, BackendError[]> = {}

    backendErrors.forEach((e) => {
        const keys = [...(e.path || [])]
        const key = keys.shift() ?? ''

        if (keys.length) {
            let currentChildren = childrenByKey[key]

            if (!currentChildren) {
                currentChildren = childrenByKey[key] = []
            }

            currentChildren.push({
                message: e.message,
                path: keys,
                isInternalServerError: e.isInternalServerError,
                isUnauthorized: e.isInternalServerError,
            })
        } else {
            if (!errors[key]) {
                errors[key] = []
            }

            errors[key]?.push(e)
        }
    })

    Object.entries(childrenByKey).forEach(([key, errz]) => {
        children[key] = createErrorCollection(
            [...errz, ...(errors[''] || [])],
            collection.isInternalServerError,
            collection.isUnauthorized,
            {
                key,
                collectDisplayed,
            }
        )
    })

    if (import.meta.env.DEV) {
        // @ts-expect-error alabala
        collection.debug = () => {
            return {
                errors: Object.keys(errors),
                children: Object.keys(children),
                length: collection.length,
                id,
            }
        }
    }

    return collection
}

export function mergeCollections(
    collections: ErrorCollection[]
): ErrorCollection {
    const id = getNextId()

    const collection: ErrorCollection = {
        get unmapped() {
            return collections.reduce((acc, curr) => {
                return [...acc, ...curr.unmapped]
            }, [] as BackendError[])
        },
        get length(): number {
            return collections.reduce((acc, curr) => {
                return acc + curr.length
            }, 0)
        },
        isInternalServerError: collections.some((c) => c.isInternalServerError),
        isUnauthorized: collections.some((c) => c.isUnauthorized),
        has(path: ErrorKeyOrKeys): boolean {
            return collections.some((c) => c.has(path))
        },
        children(path: ErrorKeyOrKeys): ErrorCollection {
            return mergeCollections(collections.map((c) => c.children(path)))
        },
        get(path: ErrorKeyOrKeys): BackendError[] {
            return collections.reduce((acc, curr) => {
                return [...acc, ...(curr.get(path) || [])]
            }, [] as BackendError[])
        },
        last(path: ErrorKeyOrKeys): string | undefined {
            // This is optimized from `collections.find((c) => c.has(path))?.last(path)`
            // To avoid calling has() which goes through the children stack
            // And once found, calling last() which also goes through the children stack
            // It goes down from 380-400 to 280-300 ms for 100k iterations
            for (let i = 0; i < collections.length; i++) {
                const collection = collections[i]

                if (!collection) {
                    continue
                }

                const last = collection.last(path)

                if (last) {
                    return last
                }
            }

            return undefined
        },
        merge(withCollection: ErrorCollection): typeof this {
            return mergeCollections([...collections, withCollection])
        },
    }

    if (import.meta.env.DEV) {
        // @ts-expect-error alabala
        collection.debug = () => {
            return {
                errors: collections.reduce((acc, curr) => {
                    // @ts-expect-error alabala
                    return [...acc, ...curr.debug().errors]
                }, [] as string[]),
                children: collections.reduce((acc, curr) => {
                    // @ts-expect-error alabala
                    return [...acc, ...curr.debug().children]
                }, [] as string[]),
                length: collection.length,
                id,
            }
        }
    }

    return collection
}

function getKeys(path: ErrorKeyOrKeys): ErrorKey[] {
    if (Array.isArray(path)) {
        return [...path]
    }

    return String(path).split('.')
}

if (import.meta.vitest) {
    const { expect, describe, test } = import.meta.vitest

    describe('createErrorCollection', () => {
        const errors: BackendError[] = [
            {
                message: 'error one',
                path: ['user', 'username'],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message: 'error two',
                path: ['accessKey'],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message: 'error three',
                path: ['accessKey'],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message: 'first roles field error',
                path: ['user', 'roles', 0],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message: 'second roles field error',
                path: ['user', 'roles', 1],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message: 'Password should contain at least one capital letter',
                path: ['user', 'password', 'first'],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message:
                    'Password should contain at least one special character',
                path: ['user', 'password', 'first'],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message: 'Password should match in both fields',
                path: ['user', 'password', 'second'],
                isInternalServerError: false,
                isUnauthorized: false,
            },
        ]

        test('A bunch of errors', () => {
            const collection = createErrorCollection(errors)

            expect(collection.last('accessKey')).toBe('error three')
            expect(collection.has('accessKey')).toBe(true)
            expect(collection.last('non-existent')).toBeUndefined()
            expect(collection.has('non-existent')).toBe(false)

            const accessKeyErrors = collection.get('accessKey')
            expect(accessKeyErrors).not.toBeUndefined()
            expect(Array.isArray(accessKeyErrors)).toEqual(true)

            if (typeof accessKeyErrors !== 'undefined') {
                if (accessKeyErrors[0]) {
                    expect(accessKeyErrors[0].message).toBe('error two')
                }

                if (accessKeyErrors[1]) {
                    expect(accessKeyErrors[1].message).toBe('error three')
                }
            }

            expect(collection.last('user.username')).toBe('error one')
            expect(collection.last('user.roles.0')).toBe(
                'first roles field error'
            )
            expect(collection.has('user.roles.0')).toBe(true)
            expect(collection.last('user.roles.1')).toBe(
                'second roles field error'
            )
            expect(collection.has('user.roles.1')).toBe(true)

            // Test errors array
            expect(collection.last(['user', 'roles', 1])).toBe(
                'second roles field error'
            )
            expect(collection.has(['user', 'roles', 1])).toBe(true)

            const userErrors = collection.children('user')
            if (typeof userErrors !== 'undefined') {
                expect(userErrors.last('roles.0')).toBe(
                    'first roles field error'
                )
                expect(userErrors.has('roles.0')).toBe(true)
                expect(userErrors.last('roles.1')).toBe(
                    'second roles field error'
                )
                expect(userErrors.has('roles.1')).toBe(true)

                const rolesErrors = userErrors.children('roles')
                if (typeof rolesErrors !== 'undefined') {
                    expect(rolesErrors.last(0)).toBe('first roles field error')
                    expect(rolesErrors.has(0)).toBe(true)
                    expect(rolesErrors.last(1)).toBe('second roles field error')
                    expect(rolesErrors.has(1)).toBe(true)
                }
            }

            const rolesErrors = collection.children('user.roles')
            if (typeof rolesErrors !== 'undefined') {
                expect(rolesErrors.last(0)).toBe('first roles field error')
                expect(rolesErrors.has(0)).toBe(true)
                expect(rolesErrors.last(1)).toBe('second roles field error')
                expect(rolesErrors.has(1)).toBe(true)
            }

            expect(collection.last('user.password.first')).toBe(
                'Password should contain at least one special character'
            )
            expect(collection.has('user.password.first')).toBe(true)
            expect(collection.last('user.password.second')).toBe(
                'Password should match in both fields'
            )
            expect(collection.has('user.password.second')).toBe(true)

            // // Test that emptyErrors is being reused whenever no errors are found
            // not anymore, because of another feature
            // const nonExistent = collection.children('user.fieldOne')
            // const nonExistentTwo = collection.children('user.fieldTwo')
            // expect(nonExistent).toEqual(nonExistentTwo)

            // not anymore, because of another feature
            // expect(collection.children('non-existent')).toStrictEqual(emptyErrors)
            // expect(collection.children('non-existent').last('faf')).toBe(undefined)
        })

        test('isInternalServerError', () => {
            const errors: BackendError[] = [
                {
                    message:
                        'Password should contain at least one capital letter',
                    path: ['user', 'password', 'first'],
                    isInternalServerError: true,
                    isUnauthorized: false,
                },
            ]

            const collection = createErrorCollection(errors)
            const password = collection.children('password')
            const first = password.children('first')

            expect(collection.isInternalServerError).toBe(true)
            expect(password.isInternalServerError).toBe(true)
            expect(first.isInternalServerError).toBe(true)

            expect(collection.isUnauthorized).toBe(false)
            expect(password.isUnauthorized).toBe(false)
            expect(first.isUnauthorized).toBe(false)
        })

        test('isUnauthorized', () => {
            const errors: BackendError[] = [
                {
                    message:
                        'Password should contain at least one capital letter',
                    path: ['user', 'password', 'first'],
                    isInternalServerError: false,
                    isUnauthorized: true,
                },
            ]

            const collection = createErrorCollection(errors)
            const password = collection.children('password')
            const first = password.children('first')

            expect(collection.isInternalServerError).toBe(false)
            expect(password.isInternalServerError).toBe(false)
            expect(first.isInternalServerError).toBe(false)

            expect(collection.isUnauthorized).toBe(true)
            expect(password.isUnauthorized).toBe(true)
            expect(first.isUnauthorized).toBe(true)
        })

        const errorOne: BackendError = {
            message: 'error one',
            path: ['MyWhateverQuery', 'user', 'username'],
            isInternalServerError: false,
            isUnauthorized: false,
        }
        const errorTwo: BackendError = {
            message: 'error two',
            path: ['user', 'email'],
            isInternalServerError: false,
            isUnauthorized: false,
        }
        const errorThree: BackendError = {
            message: 'error tre',
            path: ['something'],
            isInternalServerError: true,
            isUnauthorized: false,
        }
        const errorFour: BackendError = {
            message: 'error no path',
            path: [],
            isInternalServerError: true,
            isUnauthorized: false,
        }

        test('Test merge', () => {
            const collectionOne = createErrorCollection([errorOne])
            const collectionTwo = createErrorCollection([errorTwo])

            expect(collectionOne.last('MyWhateverQuery.user.username')).toBe(
                'error one'
            )
            expect(collectionOne.has('MyWhateverQuery.user.username')).toBe(
                true
            )
            expect(collectionTwo.last('user.email')).toBe('error two')
            expect(collectionTwo.has('user.email')).toBe(true)

            const mergedCollection = collectionOne
                .children('MyWhateverQuery')
                .merge(collectionTwo)

            expect(mergedCollection.last('user.username')).toBe('error one')
            expect(mergedCollection.has('user.username')).toBe(true)
            expect(mergedCollection.last('user.email')).toBe('error two')
            expect(mergedCollection.has('user.email')).toBe(true)
        })

        test('Test Internal Server Error', () => {
            const collection = createErrorCollection([
                errorOne,
                errorTwo,
                errorThree,
            ])

            expect(collection.last('user.email')).toBe('error two')
            // main collection
            expect(collection.isInternalServerError).toBe(true)
            expect(collection.children('user').last('email')).toBe('error two')
            // child  collection
            expect(collection.children('user').isInternalServerError).toBe(true)
            // empty collection
            expect(
                collection.children('user').children('does not exist')
                    .isInternalServerError
            ).toBe(true)
        })

        test('Test unmapped errors trickle down into children', () => {
            const collection = createErrorCollection([
                errorOne,
                errorTwo,
                errorThree,
                errorFour,
            ])

            expect(collection.last('user.email')).toBe('error two')

            expect(collection.get('')).toStrictEqual([
                {
                    message: 'error no path',
                    path: [],
                    isInternalServerError: true,
                    isUnauthorized: false,
                },
            ])

            const children = collection.children('MyWhateverQuery')

            expect(children.get('')).toStrictEqual([
                {
                    message: 'error no path',
                    path: [],
                    isInternalServerError: true,
                    isUnauthorized: false,
                },
            ])

            const nonExistentChildren = collection.children(
                'nonExistentChildren'
            )

            expect(nonExistentChildren.get('')).toStrictEqual([
                {
                    message: 'error no path',
                    path: [],
                    isInternalServerError: true,
                    isUnauthorized: false,
                },
            ])
        })

        test('Test unmapped errors', () => {
            const collection = createErrorCollection([
                errorOne,
                errorTwo,
                errorThree,
                errorFour,
            ])

            expect(collection.last('user.email')).toBe('error two')

            const children = collection.children('MyWhateverQuery')
            const children2 = collection.children('MyWhateverQuery.user')

            expect(children2.last('username')).toBe('error one')

            nextTick(() => {
                expect(collection.unmapped).toStrictEqual([
                    {
                        message: 'error tre',
                        path: ['something'],
                        isInternalServerError: true,
                        isUnauthorized: false,
                    },
                    {
                        message: 'error no path',
                        path: [],
                        isInternalServerError: true,
                        isUnauthorized: false,
                    },
                ])

                expect(children.unmapped).toStrictEqual([
                    // {
                    //     isInternalServerError: false,
                    //     message: 'error one',
                    //     path: ['user', 'username'],
                    // },
                    {
                        message: 'error no path',
                        path: [],
                        isInternalServerError: true,
                        isUnauthorized: false,
                    },
                ])
            })
        })
    })

    describe('mergeCollections', () => {
        const firstCollectionErrors: BackendError[] = [
            {
                message: 'error one',
                path: ['user', 'username'],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message: 'error two',
                path: ['accessKey'],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message: 'error three',
                path: ['accessKey'],
                isInternalServerError: false,
                isUnauthorized: false,
            },
        ]

        const secondCollectionErrors: BackendError[] = [
            {
                message: 'first roles field error',
                path: ['user', 'roles', 0],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message: 'second roles field error',
                path: ['user', 'roles', 1],
                isInternalServerError: false,
                isUnauthorized: false,
            },
        ]

        const thirdCollectionErrors: BackendError[] = [
            {
                message: 'Password should contain at least one capital letter',
                path: ['user', 'password', 'first'],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message:
                    'Password should contain at least one special character',
                path: ['user', 'password', 'first'],
                isInternalServerError: false,
                isUnauthorized: false,
            },
            {
                message: 'Password should match in both fields',
                path: ['user', 'password', 'second'],
                isInternalServerError: true,
                isUnauthorized: false,
            },
        ]

        test('Test merge collections', () => {
            const firstCollection = createErrorCollection(firstCollectionErrors)
            const secondCollection = createErrorCollection(
                secondCollectionErrors
            )
            const thirdCollection = createErrorCollection(thirdCollectionErrors)
            const mergedCollection = mergeCollections([
                firstCollection,
                secondCollection,
                thirdCollection,
            ])

            expect(firstCollection.isInternalServerError).toBe(false)
            expect(secondCollection.isInternalServerError).toBe(false)
            expect(thirdCollection.isInternalServerError).toBe(true)
            expect(mergedCollection.isInternalServerError).toBe(true)

            expect(mergedCollection.last('accessKey')).toBe('error three')
            expect(mergedCollection.has('accessKey')).toBe(true)
            expect(mergedCollection.last('non-existent')).toBeUndefined()
            expect(mergedCollection.has('non-existent')).toBe(false)

            const accessKeyErrors = mergedCollection.get('accessKey')
            expect(accessKeyErrors).not.toBeUndefined()
            expect(Array.isArray(accessKeyErrors)).toEqual(true)

            if (typeof accessKeyErrors !== 'undefined') {
                if (accessKeyErrors[0]) {
                    expect(accessKeyErrors[0].message).toBe('error two')
                }

                if (accessKeyErrors[1]) {
                    expect(accessKeyErrors[1].message).toBe('error three')
                }
            }

            expect(mergedCollection.last('user.username')).toBe('error one')

            expect(secondCollection.last('user.roles.0')).toBe(
                'first roles field error'
            )
            expect(secondCollection.has('user.roles.0')).toBe(true)
            expect(secondCollection.last('user.roles.1')).toBe(
                'second roles field error'
            )
            expect(secondCollection.has('user.roles.1')).toBe(true)

            expect(mergedCollection.last('user.roles.0')).toBe(
                'first roles field error'
            )
            expect(mergedCollection.has('user.roles.0')).toBe(true)
            expect(mergedCollection.last('user.roles.1')).toBe(
                'second roles field error'
            )
            expect(mergedCollection.has('user.roles.1')).toBe(true)

            // Test errors array
            expect(secondCollection.last(['user', 'roles', 1])).toBe(
                'second roles field error'
            )
            expect(secondCollection.has(['user', 'roles', 1])).toBe(true)
            expect(mergedCollection.has(['user', 'roles', 1])).toBe(true)
            expect(mergedCollection.last(['user', 'roles', 1])).toBe(
                'second roles field error'
            )

            const userErrors = mergedCollection.children('user')
            if (typeof userErrors !== 'undefined') {
                expect(userErrors.last('roles.0')).toBe(
                    'first roles field error'
                )
                expect(userErrors.has('roles.0')).toBe(true)
                expect(userErrors.last('roles.1')).toBe(
                    'second roles field error'
                )
                expect(userErrors.has('roles.1')).toBe(true)

                const rolesErrors = userErrors.children('roles')
                if (typeof rolesErrors !== 'undefined') {
                    expect(rolesErrors.last(0)).toBe('first roles field error')
                    expect(rolesErrors.has(0)).toBe(true)
                    expect(rolesErrors.last(1)).toBe('second roles field error')
                    expect(rolesErrors.has(1)).toBe(true)
                }
            }

            const rolesErrors = mergedCollection.children('user.roles')
            if (typeof rolesErrors !== 'undefined') {
                expect(rolesErrors.last(0)).toBe('first roles field error')
                expect(rolesErrors.has(0)).toBe(true)
                expect(rolesErrors.last(1)).toBe('second roles field error')
                expect(rolesErrors.has(1)).toBe(true)
            }

            expect(mergedCollection.last('user.password.first')).toBe(
                'Password should contain at least one special character'
            )
            expect(mergedCollection.has('user.password.first')).toBe(true)
            expect(mergedCollection.last('user.password.second')).toBe(
                'Password should match in both fields'
            )
            expect(mergedCollection.has('user.password.second')).toBe(true)

            // expect(mergedCollection.children('non-existent')).toBe(emptyErrors)
            expect(mergedCollection.children('non-existent').last('faf')).toBe(
                undefined
            )
            expect(mergedCollection.get('non-existent')).toStrictEqual([])
        })
    })

    describe('getKeys', () => {
        test('String keys', () => {
            expect(getKeys('user')).toStrictEqual(['user'])
            expect(getKeys('user.username')).toStrictEqual(['user', 'username'])
            expect(getKeys('user.username.0')).toStrictEqual([
                'user',
                'username',
                '0',
            ])
        })
        test('Array keys', () => {
            const keysInput = ['user', 'username', 0]
            const keys = getKeys(keysInput)

            // Functions using the result of getKeys would often shift items
            expect(keys).toStrictEqual(['user', 'username', 0])

            keys.shift()

            // This is to ensure getKeys always returns a copy of the array
            // So shifting is OK and recursive calls won't end up with an empty array
            expect(getKeys(keysInput)).toStrictEqual(['user', 'username', 0])
        })
    })
}
